import { cn, InfoPopoverProps, Stack, StackProps } from 'component-library';

import { IndicatorFactType } from '@/api/rest/resources/types/fact';
import { BenchmarkExplainerType } from '@/api/rest/resources/types/widget';
import { BenchmarkExplainer } from '@/components/BenchmarkExplainer';
import { LatestAnalysisFact, OneYearChangeFact } from '@/components/Facts';
import { NCCardSectionHeader } from '@/components/NCCard/NCCard';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';

export type StatTileProps = {
  headerProps?: StatTileHeaderProps;
  contentProps?: StatTileContentProps;
} & StackProps;

export const StatTile = ({ headerProps, contentProps, className, ...delegated }: StatTileProps) => {
  return (
    <Stack direction='col' {...delegated} className={cn('gap-8 lg:gap-4', className)}>
      {headerProps && <StatTileHeader {...headerProps} />}
      {contentProps && <StatTileContent {...contentProps} />}
    </Stack>
  );
};

export type StatTileHeaderProps = {
  title: string;
  supplementaryInfo?: string;
  popoverProps?: Partial<InfoPopoverProps>;
};

const StatTileHeader = ({ popoverProps, ...props }: StatTileHeaderProps) => {
  return (
    <NCCardSectionHeader
      popover={popoverProps ? { title: popoverProps?.title, body: popoverProps?.body } : undefined}
      {...props}
    />
  );
};

type StatTileContentProps = {
  oneYearChangeFactType?: IndicatorFactType | null;
  oneYearTrendFactType?: IndicatorFactType | null;
  latestAnalysisFactType?: IndicatorFactType | null;
  benchmarkFactType?: IndicatorFactType | null;
  benchmarkExplainerType?: BenchmarkExplainerType;
  showProjectionsLink?: boolean;
};

const StatTileContent = ({
  latestAnalysisFactType,
  oneYearTrendFactType,
  oneYearChangeFactType,
  benchmarkFactType,
  benchmarkExplainerType,
  showProjectionsLink = false,
}: StatTileContentProps) => {
  const facts = useOrganizationIndicatorReportingFact<number>([
    latestAnalysisFactType,
    oneYearTrendFactType,
    oneYearChangeFactType,
  ] as IndicatorFactType[]);

  const latestAnalysis = latestAnalysisFactType ? facts[`${latestAnalysisFactType}`] : undefined;
  const oneYearTrend = oneYearTrendFactType ? facts[`${oneYearTrendFactType}`] : undefined;
  const oneYearChange = oneYearChangeFactType ? facts[`${oneYearChangeFactType}`] : undefined;

  return (
    <>
      <Stack direction='row' className='flex-wrap gap-6 pb-6 border-b border-divider'>
        {latestAnalysis && oneYearTrend && (
          <LatestAnalysisFact oneYearTrend={oneYearTrend} latestAnalysis={latestAnalysis} />
        )}
        {oneYearChange && latestAnalysis && (
          <OneYearChangeFact oneYearChange={oneYearChange} latestAnalysis={latestAnalysis} />
        )}
      </Stack>
      {benchmarkFactType && latestAnalysis && (
        <BenchmarkExplainer
          benchmarkExplainerType={benchmarkExplainerType}
          benchmarkFactType={benchmarkFactType}
          latestAnalysis={latestAnalysis}
          showProjectionsLink={showProjectionsLink}
          oneYearTrend={oneYearTrend}
        />
      )}
    </>
  );
};
